import { useState, useCallback } from 'react';
import { deleteField, doc, setDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import * as Sentry from '@sentry/browser';
import dayjs from 'dayjs';
import cleanOverride from '../pages/brewery/overrideBrewery/cleanOverride';
import applyClearedOverrideDeletes from '../pages/brewery/overrideBrewery/applyClearedOverrideDeletes';

const DEBUG = process.env.NODE_ENV !== 'production';

/**
 * Custom hook for brewery update operations
 * Provides update operation with loading and error states
 * @param {string} breweryId - The ID of the brewery to update
 * @param {Object} brewery - The current brewery data (for cleaning override)
 * @returns {Object} Object containing updateBrewery function, isUpdating, and error
 */
export function useBreweryUpdate(breweryId, brewery, existingOverride = null) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Update brewery override document in Firestore
   * The logic removes empty values so the saved override object contains only values being overridden
   * @param {Object} values - The values object from formik
   * @param {Object} formikHelpers - Formik helpers object (e.g., { setSubmitting })
   * @returns {Promise<Object>} Result object with success status
   */
  const updateBrewery = useCallback(
    async (values, formikHelpers = {}) => {
      setIsUpdating(true);
      setError(null);
      try {
        // Check for Day.js objects before processing (ensure proper conversion)
        if (DEBUG && values?.oobDate && dayjs.isDayjs(values.oobDate)) {
          console.debug('[DEBUG] Converting Day.js oobDate before cleanOverride');
        }
        
        // Clean override first (converts Day.js objects to strings)
        // cleanOverride already does structuredClone internally, so we don't need to clone again
        const valuesCleaned = await cleanOverride(values, brewery);
        const valuesToSave = applyClearedOverrideDeletes(
          values,
          valuesCleaned,
          deleteField,
          existingOverride,
        );

        // Add parent brewery ID and save
        valuesToSave.parentBreweryId = breweryId;
        const overrideDocRef = doc(db, 'breweryOverrides', breweryId);
        await setDoc(overrideDocRef, valuesToSave, { merge: true });
        
        // Call setSubmitting if provided (for Formik integration)
        if (formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        
        return { success: true };
      } catch (err) {
        Sentry.captureException(err);
        setError(err.message);
        
        // Call setSubmitting if provided (for Formik integration)
        if (formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        
        return { success: false, error: err.message };
      } finally {
        setIsUpdating(false);
      }
    },
    [breweryId, brewery, existingOverride],
  );

  return {
    updateBrewery,
    isUpdating,
    error,
  };
}

